
import { cloneDeep } from 'lodash-es'
import { defineComponent, shallowRef, reactive, computed, proxyRefs, watch, PropType } from 'vue'

// Composables
import { useSyncQueryParams } from '@/use/router'
import { UseDateRange } from '@/use/date-range'
import { useRoute } from '@/use/router'
import { useDataSource } from '@/use/datasource'
import { useUql, joinQuery, createQueryEditor } from '@/use/uql'
import { useTableQuery } from '@/metrics/use-query'

// Components
import DashQueryBuilder from '@/metrics/query/DashQueryBuilder.vue'
import NewGridItemMenu from '@/metrics/NewGridItemMenu.vue'
import GroupingToggle from '@/metrics/query/GroupingToggle.vue'
import TimeseriesTable from '@/metrics/TimeseriesTable.vue'
import DashTableFormDialog from '@/metrics/DashTableFormDialog.vue'
import DashGridForTableRow from '@/metrics/DashGridForTableRow.vue'
import ApiErrorCard from '@/components/ApiErrorCard.vue'
import GridStackCard from '@/metrics/GridStackCard.vue'
import GridItemAny from '@/metrics/GridItemAny.vue'
import QuickSearch from '@/components/QuickSearch.vue'

// Misc
import { AttrKey } from '@/models/otel'
import { Dashboard, DashKind, GridRow, GridItem, TableRowData } from '@/metrics/types'

export default defineComponent({
  name: 'DashboardTable',
  components: {
    DashQueryBuilder,
    NewGridItemMenu,
    GroupingToggle,
    TimeseriesTable,
    DashTableFormDialog,
    DashGridForTableRow,
    ApiErrorCard,
    GridStackCard,
    GridItemAny,
    QuickSearch,
  },

  props: {
    dateRange: {
      type: Object as PropType<UseDateRange>,
      required: true,
    },
    dashboard: {
      type: Object as PropType<Dashboard>,
      required: true,
    },
    tableItems: {
      type: Array as PropType<GridItem[]>,
      required: true,
    },
    gridRows: {
      type: Array as PropType<GridRow[]>,
      required: true,
    },
    gridMetrics: {
      type: Array as PropType<string[]>,
      required: true,
    },
  },

  setup(props, ctx) {
    const route = useRoute()
    const dialog = shallowRef(false)

    const re = /^(where|group\s+by)\s+/i
    const baseQuery = computed(() => {
      return createQueryEditor(props.dashboard.tableQuery)
        .filter((part) => !re.test(part))
        .toString()
    })
    const editableQuery = computed(() => {
      return createQueryEditor(props.dashboard.tableQuery)
        .filter((part) => re.test(part))
        .toString()
    })

    const uql = useUql()
    watch(
      editableQuery,
      (query) => {
        uql.query = query
      },
      { immediate: true },
    )

    const tableQuery = useTableQuery(
      () => {
        if (!props.dashboard.tableQuery || !props.dashboard.tableMetrics.length) {
          return { _: undefined }
        }

        return {
          ...props.dateRange.axiosParams(),
          time_offset: props.dashboard.timeOffset,
          metric: props.dashboard.tableMetrics.map((m) => m.name),
          alias: props.dashboard.tableMetrics.map((m) => m.alias),
          query: joinQuery([uql.query, baseQuery.value]),
          min_interval: props.dashboard.minInterval,
        }
      },
      computed(() => props.dashboard.tableColumnMap),
    )
    watch(
      () => tableQuery.query,
      (query) => {
        if (query) {
          uql.setQueryInfo(query)
        }
      },
      { immediate: true },
    )

    useSyncQueryParams({
      fromQuery(queryParams) {
        queryParams.setDefault('query', editableQuery.value)

        props.dateRange.parseQueryParams(queryParams)
        tableQuery.order.parseQueryParams(queryParams)
        uql.parseQueryParams(queryParams)
      },
      toQuery() {
        return {
          ...props.dateRange.queryParams(),
          ...tableQuery.order.queryParams(),
          ...uql.queryParams(),
        }
      },
    })

    const attrKeysDs = useDataSource(() => {
      if (!props.dashboard.tableMetrics.length) {
        return undefined
      }

      const { projectId } = route.value.params
      return {
        url: `/internal/v1/metrics/${projectId}/attributes`,
        params: {
          ...props.dateRange.axiosParams(),
          metric: props.dashboard.tableMetrics.map((m) => m.name),
        },
      }
    })

    const grouping = computed({
      get() {
        return tableQuery.columns.filter((col) => col.isGroup).map((column) => column.name)
      },
      set(grouping: string[]) {
        const editor = createQueryEditor()

        for (let colName of grouping) {
          editor.groupBy(colName)
        }

        for (let part of uql.parts) {
          if (/^group by/i.test(part.query)) {
            continue
          }
          editor.add(part.query)
        }

        uql.commitEdits(editor)
      },
    })

    const tableItem = useTableRowData()

    return {
      AttrKey,
      DashKind,

      dialog,

      uql,
      tableQuery,

      attrKeysDs,
      grouping,

      tableItem,

      cloneDeep,
      reactive,
    }
  },
})

function useTableRowData() {
  const dialog = shallowRef(false)
  const activeItem = shallowRef<TableRowData>()

  const tableListeners = computed(() => {
    return {
      click(item: TableRowData) {
        activeItem.value = item
        dialog.value = true
      },
    }
  })

  return proxyRefs({
    dialog,
    active: activeItem,
    listeners: tableListeners,
  })
}
